import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getWeekStart, getWeekEnd, formatDateRange, isCurrentWeek } from '../utils/dateHelpers';
import { CALENDAR_CONSTANTS } from '../utils/constants';

dayjs.extend(isoWeek);

const CalendarNavigationContext = createContext(null);

export const useCalendarNavigation = () => {
  const context = useContext(CalendarNavigationContext);
  if (!context) {
    throw new Error('useCalendarNavigation must be used within CalendarNavigationProvider');
  }
  return context;
};

export const CalendarNavigationProvider = ({ children, basePath = '/calendar' }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam
      ? getWeekStart(dayjs(dateParam))
      : getWeekStart();
  });

  const [highlightedSlot, setHighlightedSlot] = useState(null);
  const [scrollTargetTime, setScrollTargetTime] = useState(null);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    const timeParam = searchParams.get('time');
    const trainerParam = searchParams.get('trainerId');

    if (dateParam) {
      const targetDate = dayjs(dateParam);
      const newWeekStart = getWeekStart(targetDate);

      if (!newWeekStart.isSame(currentWeekStart, 'day')) {
        setCurrentWeekStart(newWeekStart);
      }

      if (timeParam) {
        setHighlightedSlot({
          date: targetDate.format('YYYY-MM-DD'),
          time: timeParam,
          trainerId: trainerParam,
        });
        setScrollTargetTime(timeParam);
      }
    }
  }, [searchParams]);

  const navigateTo = useCallback(({ date, time, trainerId, replace = false }) => {
    const params = new URLSearchParams();

    if (date) params.set('date', dayjs(date).format('YYYY-MM-DD'));
    if (time) params.set('time', time);
    if (trainerId) params.set('trainerId', trainerId);

    setSearchParams(params, { replace });
  }, [setSearchParams]);

  const clearHighlight = useCallback(() => {
    setHighlightedSlot(null);
    setScrollTargetTime(null);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('time');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const goToNextWeek = useCallback(() => {
    setCurrentWeekStart((prev) => prev.add(1, 'week'));
  }, []);

  const goToPrevWeek = useCallback(() => {
    setCurrentWeekStart((prev) => prev.subtract(1, 'week'));
  }, []);

  const goToToday = useCallback(() => {
    setCurrentWeekStart(getWeekStart());
  }, []);

  const goToDate = useCallback((date) => {
    setCurrentWeekStart(getWeekStart(date));
  }, []);

  const currentWeekEnd = useMemo(() => {
    return getWeekEnd(currentWeekStart);
  }, [currentWeekStart]);

  const isThisWeek = useMemo(() => {
    return isCurrentWeek(currentWeekStart);
  }, [currentWeekStart]);

  const weekLabel = useMemo(() => {
    return formatDateRange(currentWeekStart, currentWeekEnd);
  }, [currentWeekStart, currentWeekEnd]);

  const value = useMemo(() => ({
    currentWeekStart,
    currentWeekEnd,
    highlightedSlot,
    scrollTargetTime,
    isThisWeek,
    weekLabel,
    basePath,

    navigateTo,
    clearHighlight,
    goToNextWeek,
    goToPrevWeek,
    goToToday,
    goToDate,
  }), [
    currentWeekStart,
    currentWeekEnd,
    highlightedSlot,
    scrollTargetTime,
    isThisWeek,
    weekLabel,
    basePath,
    navigateTo,
    clearHighlight,
    goToNextWeek,
    goToPrevWeek,
    goToToday,
    goToDate,
  ]);

  return (
    <CalendarNavigationContext.Provider value={value}>
      {children}
    </CalendarNavigationContext.Provider>
  );
};

export default CalendarNavigationContext;
